import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  FlatList,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  Pressable,
  ScrollView,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import DLOTooltip from '../../../../../modules/dlo/components/DLOTooltip';
import TableSkeleton from '../../../components/TableSkeleton';
import styles from '../OrderRulesReport.styles';

interface SalesDetailsTabProps {
  loading: boolean;
  rows: Array<Record<string, any>>;
  /** Server-side sort state — column key + direction currently applied via the API. */
  sort: { col: string | null; dir: 'asc' | 'desc' | null };
  /** Fires the sortBy/sortOrder API call. */
  onSort: (colKey: string) => void;
  /** Pagination bar, rendered directly under the table. */
  pagination?: React.ReactNode;
}

interface TableColumn {
  key: string;
  label: string;
  align?: 'left' | 'right' | 'center';
  minWidth?: number;
}

type SortDir = 'asc' | 'desc' | null;

interface SortState {
  col: string | null;
  dir: SortDir;
}

const salesColumns: TableColumn[] = [
  { key: 'region', label: 'Region', minWidth: 120, align: 'left' },
  { key: 'market', label: 'Market', minWidth: 170, align: 'left' },
  { key: 'location', label: 'Location', minWidth: 150, align: 'left' },
  { key: 'customerBp', label: 'Customer BP', minWidth: 130, align: 'right' },
  { key: 'customer', label: 'Customer', minWidth: 200, align: 'left' },
  { key: 'keyAccount', label: 'Key Account', minWidth: 160, align: 'left' },
  { key: 'materialNumber', label: 'Material No.', minWidth: 130, align: 'right' },
  { key: 'packageName', label: 'Package', minWidth: 180, align: 'left' },
  { key: 'description', label: 'Material Description', minWidth: 250, align: 'left' },
  { key: 'layer', label: 'Cases / Layer', minWidth: 160, align: 'right' },
  { key: 'avgCases', label: 'Avg Cases / Wk', minWidth: 180, align: 'right' },
];

const salesHeaderTooltips: Record<string, string> = {
  layer: 'Based on order rules quantity if it exists, otherwise the large pallet build quantity.',
  avgCases: 'Average cases sold per week across the selected timeframe.',
};

const TABLE_MIN_WIDTH = salesColumns.reduce((acc, col) => acc + (col.minWidth ?? 120), 0);

/** Height of each data row — must be fixed so FlatList.getItemLayout works */
const ROW_HEIGHT = 44;
/** Maximum number of rows visible before the body starts scrolling vertically */
const MAX_VISIBLE_ROWS = 12;

const NUMERIC_KEYS = new Set(['layer', 'avgCases']);

const compareValues = (a: any, b: any, key: string): number => {
  if (NUMERIC_KEYS.has(key)) return Number(a ?? 0) - Number(b ?? 0);
  return String(a ?? '').localeCompare(String(b ?? ''));
};

const SalesDetailsTab: React.FC<SalesDetailsTabProps> = ({ loading, rows, sort, onSort: handleSort, pagination }) => {
  const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

  // ── Scroll sync refs (horizontal: header ↔ body) ──────────────────────
  const headerScrollRef = useRef<ScrollView>(null);
  const bodyScrollRef = useRef<ScrollView>(null);
  const isHeaderScrolling = useRef(false);
  const isBodyScrolling = useRef(false);

  const onHeaderHorizontalScroll = useCallback((e: NativeSyntheticEvent<NativeScrollEvent>) => {
    if (isBodyScrolling.current) return;
    isHeaderScrolling.current = true;
    bodyScrollRef.current?.scrollTo({ x: e.nativeEvent.contentOffset.x, animated: false });
    isHeaderScrolling.current = false;
  }, []);

  const onBodyHorizontalScroll = useCallback((e: NativeSyntheticEvent<NativeScrollEvent>) => {
    if (isHeaderScrolling.current) return;
    isBodyScrolling.current = true;
    headerScrollRef.current?.scrollTo({ x: e.nativeEvent.contentOffset.x, animated: false });
    isBodyScrolling.current = false;
  }, []);

  // Rows arrive already sorted by the backend when a sort is active (sortBy/sortOrder
  // in the API request). When no column sort is applied, fall back to the default
  // Region -> Market -> Location -> Customer (A-Z) ordering on the client.
  const sortedRows = useMemo(() => {
    if (sort.col) return rows;
    return [...rows].sort((a, b) =>
      compareValues(a.region, b.region, 'region') ||
      compareValues(a.market, b.market, 'market') ||
      compareValues(a.location, b.location, 'location') ||
      compareValues(a.customer, b.customer, 'customer'),
    );
  }, [rows, sort.col]);

  // Fixed-height body: shrink to fit for few rows, cap at MAX_VISIBLE_ROWS
  const bodyHeight = useMemo(
    () => Math.min(sortedRows.length, MAX_VISIBLE_ROWS) * ROW_HEIGHT,
    [sortedRows.length],
  );

  const getItemLayout = useCallback(
    (_: any, index: number) => ({ length: ROW_HEIGHT, offset: ROW_HEIGHT * index, index }),
    [],
  );

  const isRowHoverable = Platform.OS === 'web';
  const contentWidthStyle = useMemo(() => ({ width: TABLE_MIN_WIDTH }), []);

  const renderRow = useCallback(
    ({ item: row, index: rowIndex }: { item: Record<string, any>; index: number }) => {
      const isHovered = hoveredRowIndex === rowIndex;
      return (
        <Pressable
          style={[styles.tableDataRow, isHovered && styles.tableDataRowHover]}
          onHoverIn={isRowHoverable ? () => setHoveredRowIndex(rowIndex) : undefined}
          onHoverOut={isRowHoverable ? () => setHoveredRowIndex(null) : undefined}
        >
          {salesColumns.map((column) => {
            const value = row[column.key];
            return (
              <View
                key={column.key}
                style={[
                  styles.tableDataCell,
                  {
                    width: column.minWidth ?? 120,
                    minWidth: column.minWidth ?? 120,
                    maxWidth: column.minWidth ?? 120,
                    overflow: 'hidden',
                  },
                  styles.alignCenter,
                ]}
              >
                {column.key === 'location' ? (
                  <TouchableOpacity activeOpacity={0.75} onPress={() => {}}>
                    <Text style={styles.tableLinkText} numberOfLines={1}>
                      {String(value ?? '-')}
                    </Text>
                  </TouchableOpacity>
                ) : (
                  <Text style={styles.tableDataText} numberOfLines={1}>
                    {String(value ?? '-')}
                  </Text>
                )}
              </View>
            );
          })}
        </Pressable>
      );
    },
    [hoveredRowIndex, isRowHoverable],
  );

  if (loading) {
    return <TableSkeleton rows={10} />;
  }

  if (!rows || rows.length === 0) {
    return (
      <View style={styles.emptyStateWrap}>
        <Text style={styles.emptyStateText}>No sales history for the current selection.</Text>
      </View>
    );
  }

  return (
    <View>
      {/*
       * ── STICKY COLUMN HEADER ──────────────────────────────────────────────
       * Lives OUTSIDE the fixed-height body container so it is always visible.
       * Horizontal offset is kept in sync with the body ScrollView via refs.
       */}
      <ScrollView
        ref={headerScrollRef}
        horizontal
        showsHorizontalScrollIndicator={false}
        scrollEventThrottle={16}
        onScroll={onHeaderHorizontalScroll}
        bounces={false}
        contentContainerStyle={contentWidthStyle}
      >
        <View style={styles.tableHeaderRow}>
          {salesColumns.map((column) => {
            const isActive = sort.col === column.key;
            return (
              <TouchableOpacity
                key={column.key}
                style={[
                  styles.tableHeaderCell,
                  {
                    width: column.minWidth ?? 120,
                    minWidth: column.minWidth ?? 120,
                    maxWidth: column.minWidth ?? 120,
                  },
                  styles.tableHeaderCellAlignCenter,
                ]}
                onPress={() => handleSort(column.key)}
                activeOpacity={0.7}
                accessibilityLabel={`Sort by ${column.label}`}
              >
                <View style={styles.maxPotentialHeaderTextWrap}>
                  <Text style={styles.tableHeaderText} numberOfLines={1}>
                    {column.label}
                  </Text>
                  {salesHeaderTooltips[column.key] ? (
                    <DLOTooltip content={salesHeaderTooltips[column.key]} trigger="hover" position="top" maxWidth={300}>
                      <View style={styles.maxPotentialHeaderInfoIcon}>
                        <MaterialIcons name="info-outline" size={12} color="#818181" />
                      </View>
                    </DLOTooltip>
                  ) : null}
                </View>
                <MaterialIcons
                  name={isActive ? (sort.dir === 'asc' ? 'keyboard-arrow-up' : 'keyboard-arrow-down') : 'unfold-more'}
                  size={14}
                  color={isActive ? '#005CBC' : '#9E9E9E'}
                  style={styles.tableHeaderSortIcon}
                />
              </TouchableOpacity>
            );
          })}
        </View>
      </ScrollView>

      {/*
       * ── FIXED-HEIGHT BODY CONTAINER ──────────────────────────────────────
       * The pixel height is capped — only content INSIDE this View scrolls.
       * Horizontal scroll (ScrollView) + vertical scroll (FlatList) both
       * happen here, mirroring the OnePager bodyContainer pattern exactly.
       */}
      <View style={{ height: bodyHeight, overflow: 'hidden' }}>
        <ScrollView
          ref={bodyScrollRef}
          horizontal
          showsHorizontalScrollIndicator
          scrollEventThrottle={16}
          onScroll={onBodyHorizontalScroll}
          bounces={false}
          contentContainerStyle={contentWidthStyle}
        >
          <FlatList
            data={sortedRows}
            renderItem={renderRow}
            keyExtractor={(_, index) => `sales-row-${index}`}
            getItemLayout={getItemLayout}
            showsVerticalScrollIndicator
            removeClippedSubviews
            maxToRenderPerBatch={20}
            windowSize={5}
            initialNumToRender={15}
          />
        </ScrollView>
      </View>
      {pagination}
    </View>
  );
};

export default SalesDetailsTab;


